import { RootState } from '@/lib/store';
import { GenericError } from '@/models/generic-error';
import { PaginatedResponse } from '@/models/pagination/paginated-response.dto';
import { LuckySpinReward } from '@/models/user-reward/lucky-spin/lucky-spin-rewards';
import { LuckySpinsRewardsRequestDto } from '@/services/dto/lucky-spin-rewards/lucky-spins-rewards-request.dto';
import { CoinID } from '@starsoft/common/enums';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { LuckySpinRewardsQueryKeys } from '../../queryKeys';
import { UseLuckySpinRewardsByLuckySpinQueryKeyProps } from './props';
import { fetchLuckySpinRewards } from './service';

export function useLuckySpinsRewardsByLuckySpin({
  id,
}: LuckySpinsRewardsRequestDto) {
  const queryKey: UseLuckySpinRewardsByLuckySpinQueryKeyProps = [
    LuckySpinRewardsQueryKeys.ByLuckySpin,
    { id, limit: 30, page: 1 },
  ];

  const prize = useSelector((state: RootState) => state.luckSpin.prize);

  const { data, error, refetch, isPending, fetchStatus } = useQuery<
    PaginatedResponse<LuckySpinReward>,
    AxiosError<GenericError>,
    PaginatedResponse<LuckySpinReward>,
    UseLuckySpinRewardsByLuckySpinQueryKeyProps
  >({
    queryKey,
    queryFn: fetchLuckySpinRewards,
    enabled: !!id,
    staleTime: 5 * 1000,
  });

  const prizeIndex = useMemo(
    () =>
      data?.data?.findIndex(
        spinsReward => spinsReward?.id?.toString() == prize?.id?.toString(),
      ),
    [data, prize],
  );

  const biggestPrize = useMemo(
    () =>
      data?.data?.find(
        spin => spin?.coin?.id?.toString() == CoinID.BTC.toString(),
      ),
    [data],
  );

  return {
    spinsRewards: data,
    prizeIndex: Number(prizeIndex) + 48,
    error,
    refetch,
    isLoading: isPending && fetchStatus != 'idle',
    biggestPrize,
  };
}
